// sapi-admin/src/components/NetworkStatus.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Wifi, WifiOff, AlertCircle, RefreshCw } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

// Détection si on est en natif
const isNative = Capacitor.isNativePlatform();

// Plugin Network (chargé dynamiquement)
let Network = null;
if (isNative) {
  try {
    const networkModule = require('@capacitor/network');
    Network = networkModule.Network;
    console.log('✅ Plugin Network chargé');
  } catch (error) {
    console.warn('⚠️ Plugin Network non disponible:', error.message);
  }
}

const NetworkStatus = ({ className = '', onConnectivityChange }) => {
  const [isConnected, setIsConnected] = useState(true);
  const [connectionType, setConnectionType] = useState('unknown');
  const [wasOffline, setWasOffline] = useState(false);
  const isInitialMount = useRef(true); // ✅ Ignorer le premier appel

  // ✅ Ref miroir de isConnected : permet à updateNetworkStatus de lire la
  // valeur courante SANS être recréée à chaque changement de state, ce qui
  // évite que le useEffect se relance et ré-enregistre des listeners réseau
  // en double (cause des toasts de connectivité qui reviennent en boucle).
  const isConnectedRef = useRef(true);
  useEffect(() => {
    isConnectedRef.current = isConnected;
  }, [isConnected]);

  // Mettre à jour le statut réseau — référence STABLE (pas de dépendance sur isConnected)
  const updateNetworkStatus = useCallback(async () => {
    try {
      let connected = false;
      let type = 'unknown';

      if (isNative && Network) {
        const status = await Network.getStatus();
        connected = status.connected;
        type = status.connectionType || 'unknown';
      } else {
        connected = navigator.onLine;
        type = connected ? 'wifi' : 'none';
      }

      const previouslyConnected = isConnectedRef.current;

      // ✅ Ignorer le premier appel (montage)
      if (isInitialMount.current) {
        isInitialMount.current = false;
        isConnectedRef.current = connected;
        setIsConnected(connected);
        setConnectionType(type);
        return;
      }

      // Rien à faire si le statut n'a pas réellement changé
      if (previouslyConnected === connected) {
        setConnectionType(type);
        return;
      }

      // Détecter le retour en ligne
      if (!previouslyConnected && connected) {
        console.log('📶 Réseau restauré ! Synchronisation...');
        setWasOffline(true);
        if (onConnectivityChange) {
          onConnectivityChange(true);
        }
        setTimeout(() => setWasOffline(false), 2000);
      } else if (previouslyConnected && !connected) {
        console.log('📶 Réseau: Déconnecté');
        if (onConnectivityChange) {
          onConnectivityChange(false);
        }
      }

      isConnectedRef.current = connected;
      setIsConnected(connected);
      setConnectionType(type);

    } catch (error) {
      console.error('❌ Erreur statut réseau:', error);
      const online = navigator.onLine;
      isConnectedRef.current = online;
      setIsConnected(online);
      setConnectionType(online ? 'wifi' : 'none');
    }
    // ⚠️ Volontairement AUCUNE dépendance sur isConnected ici : c'est ce qui
    // garantit que cette fonction — et donc le useEffect ci-dessous — ne
    // soit (re)créée qu'une seule fois.
  }, [onConnectivityChange]);

  // Initialisation et listeners — ne se relance plus en boucle
  useEffect(() => {
    updateNetworkStatus();

    if (!isNative) {
      const handleOnline = () => {
        console.log('📶 Web: Connecté');
        if (!isInitialMount.current && !isConnectedRef.current && onConnectivityChange) {
          onConnectivityChange(true);
        }
        isConnectedRef.current = true;
        setIsConnected(true);
        setConnectionType('wifi');
      };
      const handleOffline = () => {
        console.log('📶 Web: Déconnecté');
        if (!isInitialMount.current && isConnectedRef.current && onConnectivityChange) {
          onConnectivityChange(false);
        }
        isConnectedRef.current = false;
        setIsConnected(false);
        setConnectionType('none');
      };

      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }

    if (isNative && Network) {
      let listener = null;
      let cancelled = false;

      Network.addListener('networkStatusChange', (status) => {
        const connected = status.connected;
        const previouslyConnected = isConnectedRef.current;

        if (previouslyConnected === connected) return; // pas de vrai changement

        console.log(`📶 Réseau changé: ${connected ? 'Connecté' : 'Déconnecté'}`);

        if (!isInitialMount.current && onConnectivityChange) {
          onConnectivityChange(connected);
        }

        isConnectedRef.current = connected;
        setIsConnected(connected);
        setConnectionType(status.connectionType || 'unknown');
      }).then((l) => {
        // ✅ Si l'effet a déjà été nettoyé avant la résolution de la promesse,
        // on retire immédiatement le listener au lieu de le laisser traîner.
        if (cancelled) {
          l.remove();
        } else {
          listener = l;
        }
      });

      return () => {
        cancelled = true;
        if (listener) {
          listener.remove();
        }
      };
    }
    // ✅ updateNetworkStatus est maintenant stable → cet effet ne tourne
    // qu'au montage (+ si onConnectivityChange change de référence).
  }, [updateNetworkStatus, onConnectivityChange]);

  // Forcer une vérification manuelle
  const handleRefresh = () => {
    updateNetworkStatus();
  };

  const getStatusText = () => {
    if (isConnected) {
      if (connectionType === 'wifi') return 'Wi-Fi';
      if (connectionType === 'cellular') return 'Mobile';
      if (connectionType === 'ethernet') return 'Réseau';
      return 'Connecté';
    }
    return 'Hors ligne';
  };

  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-300 ${
        isConnected
          ? 'bg-emerald-500/10 border border-emerald-500/20'
          : 'bg-red-500/10 border border-red-500/20 animate-pulse'
      } ${wasOffline ? 'ring-2 ring-emerald-400 ring-offset-2 ring-offset-[#0a0f1e]' : ''} ${className}`}
      title={isConnected ? `Connecté (${getStatusText()})` : 'Aucune connexion réseau disponible'}
      onClick={handleRefresh}
    >
      {isConnected ? (
        <>
          <Wifi size={16} className="text-emerald-400" strokeWidth={2} />
          <span className="text-[10px] font-medium hidden sm:inline text-emerald-400">
            {getStatusText()}
          </span>
          {wasOffline && (
            <RefreshCw size={12} className="text-emerald-400 animate-spin" />
          )}
        </>
      ) : (
        <>
          <WifiOff size={16} className="text-red-400" strokeWidth={2} />
          <span className="text-[10px] font-medium hidden sm:inline text-red-400">
            {getStatusText()}
          </span>
          <AlertCircle size={12} className="text-red-400 animate-pulse" />
        </>
      )}
    </div>
  );
};

export default NetworkStatus;